import { useCallback, useMemo, useState } from 'react';
import type {
  DocumentGenerationEngineDTO,
  DocumentGenerationKindDTO,
  DocumentGenerationSpecDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createDocumentGeneration,
  getDocumentGeneration,
  getGeneratedDocumentVersions,
} from '../services/document-generation.api';

type PollBudget = {
  baseIntervalMs: number;
  maxUpdates: number;
  slowIntervalMs: number;
  slowdownAfterUpdates: number;
};

// Voie script : ~2 min à 2 s avant de ralentir, puis plafond global ~13 min — couvre
// le budget backend (300 s) et ses retries worker avant de cesser de solliciter l'API.
const SCRIPT_POLL_BUDGET: PollBudget = {
  baseIntervalMs: 2_000,
  maxUpdates: 200,
  slowIntervalMs: 5_000,
  slowdownAfterUpdates: 60,
};
// Moteur blocks : 1 s pendant la rédaction pour voir le document se construire
// (2 min), puis 3 s jusqu'à ~12 min au total.
const BLOCKS_POLL_BUDGET: PollBudget = {
  baseIntervalMs: 1_000,
  maxUpdates: 320,
  slowIntervalMs: 3_000,
  slowdownAfterUpdates: 120,
};
const GENERATION_POLL_MAX_CONSECUTIVE_FAILURES = 5;

type GenerationStatus = DocumentGenerationStatusResponseDTO['status'];

export function isTerminalGenerationStatus(status: GenerationStatus | undefined) {
  return status === 'dead' || status === 'failed' || status === 'succeeded';
}

export function resolveGenerationPollInterval(input: {
  dataUpdateCount: number;
  engine?: DocumentGenerationEngineDTO;
  fetchFailureCount: number;
  status: GenerationStatus | undefined;
}): number | false {
  if (isTerminalGenerationStatus(input.status)) {
    return false;
  }
  if (input.fetchFailureCount >= GENERATION_POLL_MAX_CONSECUTIVE_FAILURES) {
    return false;
  }
  const budget = input.engine === 'blocks' ? BLOCKS_POLL_BUDGET : SCRIPT_POLL_BUDGET;
  if (input.dataUpdateCount >= budget.maxUpdates) {
    return false;
  }
  return input.dataUpdateCount >= budget.slowdownAfterUpdates
    ? budget.slowIntervalMs
    : budget.baseIntervalMs;
}

/**
 * Le polling s'est arrêté alors que la génération n'est pas terminée : budget épuisé
 * ou échecs réseau consécutifs. L'UI doit alors proposer de relancer, jamais rester figée.
 */
export function isGenerationPollingExhausted(input: {
  dataUpdateCount: number;
  engine?: DocumentGenerationEngineDTO;
  fetchFailureCount: number;
  status: GenerationStatus | undefined;
}) {
  return !isTerminalGenerationStatus(input.status) && resolveGenerationPollInterval(input) === false;
}

export function documentGenerationQueryKey(conversationId?: string, generationId?: string) {
  return ['document-generation', conversationId, generationId] as const;
}

export function useDocumentGenerationStatus(conversationId?: string, generationId?: string) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => documentGenerationQueryKey(conversationId, generationId),
    [conversationId, generationId],
  );
  // Le compteur de mises à jour du cache est cumulatif : « Réessayer » repart d'un budget neuf.
  const [pollOffset, setPollOffset] = useState(0);
  const query = useQuery({
    enabled: Boolean(conversationId && generationId),
    queryFn: () => getDocumentGeneration(conversationId!, generationId!),
    queryKey,
    refetchInterval: (current) => resolveGenerationPollInterval({
      dataUpdateCount: current.state.dataUpdateCount - pollOffset,
      engine: current.state.data?.engine,
      fetchFailureCount: current.state.fetchFailureCount,
      status: current.state.data?.status,
    }),
  });
  // `dataUpdatedAt` change à chaque réponse : le rendu suit donc le compteur lu ici.
  const dataUpdateCount = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  const pollingExhausted = Boolean(conversationId && generationId) && (
    isGenerationPollingExhausted({
      dataUpdateCount: dataUpdateCount - pollOffset,
      engine: query.data?.engine,
      fetchFailureCount: query.failureCount,
      status: query.data?.status,
    })
    || (query.isError && !isTerminalGenerationStatus(query.data?.status))
  );
  const { refetch } = query;
  const restartPolling = useCallback(() => {
    setPollOffset(queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0);
    return refetch();
  }, [queryClient, queryKey, refetch]);

  return { ...query, pollingExhausted, restartPolling };
}

export function useGeneratedDocumentVersions(input: {
  conversationId?: string;
  documentId?: string;
  enabled: boolean;
  page: number;
  pageSize?: number;
}) {
  const pageSize = input.pageSize ?? 20;
  return useQuery({
    enabled: input.enabled && Boolean(input.conversationId && input.documentId),
    queryFn: () => getGeneratedDocumentVersions(input.conversationId!, input.documentId!, {
      page: input.page,
      pageSize,
    }),
    queryKey: ['generated-document-versions', input.conversationId, input.documentId, input.page, pageSize],
  });
}

export function useDocumentGeneration(conversationId?: string) {
  const [generationId, setGenerationId] = useState<string | null>(null);
  const createMutation = useMutation({
    mutationFn: (input: { kind: DocumentGenerationKindDTO; spec: DocumentGenerationSpecDTO }) => {
      if (!conversationId) {
        throw new Error('Conversation non synchronisée.');
      }
      return createDocumentGeneration({ conversationId, ...input });
    },
    onSuccess: (result) => setGenerationId(result.generationId),
  });
  const statusQuery = useDocumentGenerationStatus(conversationId, generationId ?? undefined);

  return {
    create: createMutation.mutateAsync,
    error: createMutation.error ?? statusQuery.error,
    generationId,
    isPending: createMutation.isPending || statusQuery.data?.status === 'pending' || statusQuery.data?.status === 'generating',
    reset: () => {
      setGenerationId(null);
      createMutation.reset();
    },
    status: statusQuery.data ?? null,
  };
}
